import { BranchNode, BranchType } from '../../../types/definition';
import { SchemaBuilder } from '../../schema-builder/SchemaBuilder';
import { InputMappingEditor } from './InputMappingEditor';

interface BranchNodePropertiesProps {
  node: BranchNode;
  onChange: (updates: Partial<BranchNode>) => void;
  /**
   * Inspect, do not edit (task 881). The decisions list below was already
   * read-only in both modes; this makes the rest of the fields match when the
   * canvas that opened the panel is itself read-only.
   */
  readOnly?: boolean;
}

const BRANCH_TYPES: { value: BranchType; label: string; description: string }[] = [
  { value: 'conditional', label: 'Conditional', description: 'Routes to different paths based on condition' },
  { value: 'parallel', label: 'Parallel', description: 'Runs multiple paths simultaneously' },
  { value: 'loop', label: 'Loop', description: 'Repeats until exit condition is met' },
];

export function BranchNodeProperties({ node, onChange, readOnly = false }: BranchNodePropertiesProps) {
  return (
    <div className="space-y-4" aria-readonly={readOnly || undefined}>
      {/* Node Name */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Name
        </label>
        <input
          type="text"
          value={node.name || ''}
          onChange={readOnly ? undefined : (e) => onChange({ name: e.target.value })}
          disabled={readOnly}
          placeholder="Branch"
          className="block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm
                     focus:border-orange-500 focus:ring-orange-500
                     dark:bg-gray-700 dark:text-white text-sm"
        />
      </div>

      {/* Branch Type Selector */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Branch Type <span className="text-red-500">*</span>
        </label>
        <select
          value={node.branchType || ''}
          onChange={readOnly ? undefined : (e) => onChange({ branchType: e.target.value as BranchType })}
          disabled={readOnly}
          className="block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm
                     focus:border-orange-500 focus:ring-orange-500
                     dark:bg-gray-700 dark:text-white text-sm"
        >
          <option value="">Select type...</option>
          {BRANCH_TYPES.map((type) => (
            <option key={type.value} value={type.value}>
              {type.label}
            </option>
          ))}
        </select>
        {node.branchType && (
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            {BRANCH_TYPES.find(t => t.value === node.branchType)?.description}
          </p>
        )}
      </div>

      {/* Conditional-specific: Condition Expression */}
      {node.branchType === 'conditional' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Condition <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={node.condition || ''}
            onChange={readOnly ? undefined : (e) => onChange({ condition: e.target.value })}
            disabled={readOnly}
            placeholder="{{nodes.previous.result}} === 'success'"
            className="block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm
                       focus:border-orange-500 focus:ring-orange-500
                       dark:bg-gray-700 dark:text-white text-sm font-mono"
          />
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            JavaScript expression that evaluates to true/false
          </p>
        </div>
      )}

      {/* Routing decisions, READ-ONLY (task 774).
          This is the form seeded branches actually use, and until now the
          editor could neither show it nor validate against it — a branch that
          routes perfectly well looked empty and was reported as broken. Editing
          them is deliberately out of scope here; seeing them is the fix. */}
      {node.branchType === 'conditional' && (node.decisions?.length ?? 0) > 0 && (
        <div data-testid="branch-decisions">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Decisions
          </label>
          <ul className="space-y-2">
            {node.decisions!.map((decision, index) => (
              <li
                key={decision.id || index}
                data-testid="branch-decision"
                className="rounded-md border border-gray-200 bg-gray-50 p-2 text-xs dark:border-gray-600 dark:bg-gray-900/40"
              >
                <div className="font-medium text-gray-900 dark:text-white">
                  {decision.name || decision.id}
                </div>
                <div className="mt-0.5 font-mono text-gray-600 dark:text-gray-300">
                  {/* An arm with no condition is the engine's explicit default. */}
                  {decision.condition || 'default (no condition)'}
                </div>
                <div className="mt-0.5 text-gray-500 dark:text-gray-400">
                  → {decision.targetNodeId}
                </div>
              </li>
            ))}
          </ul>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Read-only here — edit decisions in the JSON view.
          </p>
        </div>
      )}

      {/* Loop-specific: Until Condition */}
      {node.branchType === 'loop' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Until Condition <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={node.until || ''}
            onChange={readOnly ? undefined : (e) => onChange({ until: e.target.value })}
            disabled={readOnly}
            placeholder="{{context.iterations}} >= 5"
            className="block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm
                       focus:border-orange-500 focus:ring-orange-500
                       dark:bg-gray-700 dark:text-white text-sm font-mono"
          />
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Loop exits when this condition is true
          </p>
        </div>
      )}

      {/* Branches Configuration (for conditional/parallel) */}
      {(node.branchType === 'conditional' || node.branchType === 'parallel') && (
        <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Branches
          </label>
          <textarea
            value={node.branches ? JSON.stringify(node.branches, null, 2) : ''}
            onChange={
              readOnly
                ? undefined
                : (e) => {
                    try {
                      const branches = e.target.value ? JSON.parse(e.target.value) : undefined;
                      onChange({ branches });
                    } catch {
                      // Invalid JSON - don't update
                    }
                  }
            }
            disabled={readOnly}
            placeholder={`[
  {"id": "true", "name": "True Path", "targetNodeId": "nodeA"},
  {"id": "false", "name": "False Path", "targetNodeId": "nodeB"}
]`}
            rows={6}
            className="block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm
                       focus:border-orange-500 focus:ring-orange-500
                       dark:bg-gray-700 dark:text-white text-sm font-mono"
          />
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Define branch paths with target nodes
          </p>
        </div>
      )}

      {/* Input Schema */}
      <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
        <SchemaBuilder
          label="Input Schema"
          value={node.input ?? null}
          readOnly={readOnly}
          onChange={(schema) => onChange({ input: schema ?? undefined })}
        />
      </div>

      {/* Input Mapping */}
      <div>
        <InputMappingEditor
          value={node.inputMapping}
          readOnly={readOnly}
          onChange={(mapping) => onChange({ inputMapping: mapping })}
          schema={node.input}
        />
      </div>

      {/* Output Schema */}
      <div>
        <SchemaBuilder
          label="Output Schema"
          value={node.output ?? null}
          readOnly={readOnly}
          onChange={(schema) => onChange({ output: schema ?? undefined })}
        />
      </div>
    </div>
  );
}
